import { cx } from '@linaria/core';
import React, { useEffect, useState } from 'react';
import { flex } from 'styles/utils';
import { extractThumbUrl } from 'utils/inputs';
import { useGetImage } from 'hooks/useGetImage';
import { cropImage } from 'modules/InputsManager/helpers/cropImage';
import { RegionInfo } from 'clarifai-web-grpc/proto/clarifai/api/resources_pb';
import { carouselItem } from 'components/CarouselThumb/CarouselThumb.styles';
import { TextThumb } from 'components/CarouselThumb/TextThumb';

interface CarouselThumbProps extends Omit<React.HTMLAttributes<HTMLButtonElement>, 'onSelect'> {
  input: CF.API.Inputs.Input;
  index: number;
  isSelected?: boolean;
  altPrefix: string;
  onSelect?: (inputId: number, selectedInput: CF.API.Inputs.Input) => void;
  width?: number;
  className?: string;
  disabled?: boolean;
  isAnnotationsModeActive?: boolean;
  regionInfo?: RegionInfo;
}

export const CarouselThumb: React.FC<CarouselThumbProps> = ({
  index,
  input,
  altPrefix,
  isSelected = false,
  onSelect = () => {},
  width,
  className,
  disabled,
  isAnnotationsModeActive,
  regionInfo,
  ...rest
}) => {
  const [cropSrc, setCropSrc] = useState<undefined | string>();
  const { image: src } = useGetImage({
    extractImage: async () => extractThumbUrl(input as CF.API.Inputs.ImageInput, 'tiny'),
    condition: 'image' in input.data || 'video' in input.data,
  });

  useEffect(() => {
    if (isAnnotationsModeActive && src) {
      cropImage(src, setCropSrc, regionInfo);
    }
  }, [src, regionInfo]);

  const handleOnSelect = (): void => onSelect(index, input);

  if (!src && ('image' in input.data || 'video' in input.data)) return null;

  if ('audio' in input.data) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={handleOnSelect}
        className={cx(carouselItem, isSelected && 'selected', flex.init, flex.alignItems.center, flex.justifyContent.center, className)}
        {...rest}
      >
        <div style={{ backgroundImage: 'url(/waves.png)' }} aria-label={`input ${input.id}`} role="presentation" />
      </button>
    );
  }

  if ('image' in input.data) {
    return (
      <button {...rest} type="button" className={cx(carouselItem, isSelected && 'selected', className)} onClick={handleOnSelect} disabled={disabled}>
        <img
          alt={`${altPrefix} thumbnail ${index + 1}`}
          src={isAnnotationsModeActive ? cropSrc : src}
          aria-label={`${altPrefix} thumbnail ${index + 1}`}
          role="presentation"
          width={width}
        />
      </button>
    );
  }

  if ('video' in input.data) {
    const _input = input as CF.API.Inputs.VideoInput;
    const isVideoBase64 = 'base64' in _input.data.video;
    return (
      <button {...rest} type="button" className={cx(carouselItem, isSelected && 'selected', className)} onClick={handleOnSelect} disabled={disabled}>
        {isVideoBase64 ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={src} aria-label={`input ${input.id}`} />
        ) : (
          <img
            alt={`${altPrefix} thumbnail ${index + 1}`}
            src={src}
            aria-label={`${altPrefix} thumbnail ${index + 1}`}
            role="presentation"
            width={width}
          />
        )}
      </button>
    );
  }

  if ('text' in input.data) {
    return (
      <TextThumb
        input={input as CF.API.Inputs.TextInput}
        onClick={handleOnSelect}
        isSelected={isSelected}
        className={className}
        disabled={disabled}
        {...rest}
      />
    );
  }

  return null;
};
